import React from 'react'
import { inject, observer } from 'mobx-react'
import { withTranslation } from 'react-i18next'
import classnames from 'classnames'
import { placeAdministrativeBoundary } from 'constants.js'
import PaneHeader from '../panes/PaneHeader'
import BoundaryListSearch from './BoundaryListSearch'
import BoundaryListBrowse from './BoundaryListBrowse'

const BoundaryListPane = ({
  boundaryList,
  group,
  selectionStore,
  uiStore,
  rightSidebarStore,
  dataStore,
  t
}) => {
  const isLoading = boundaryList.root.fetching_data
  const selectedCount = (boundaryList.selectedBoundaries || []).length

  const handleClose = () => {
    selectionStore.clear()
  }

  const handleSetZipCodes = async (useZipCodes) => {
    if (isLoading) return
    if (boundaryList.useZipCodes === useZipCodes) return
    // Clear selected boundaries to prevent hanging when toggling
    boundaryList.selectedBoundaries = []
    boundaryList.selectedBoundary = null
    boundaryList.search = ''
    boundaryList.useZipCodes = useZipCodes
    await boundaryList.root.loadSubBoundaries(boundaryList.useZipCodes)
  }

  const handleConfirmAdd = async () => {
    const targetGroup = group || (selectionStore.data && selectionStore.data.group)
    const zipCodes = boundaryList.selectedBoundaries.filter(boundary => boundary.is_zip_code)
    const adminBoundaries = boundaryList.selectedBoundaries.filter(boundary => !boundary.is_zip_code)

    try {
      boundaryList.savingBoundaries = true
      await boundaryList.add()
      uiStore.addNotification({
        text: t('BOUNDARY.BOUNDARY_SAVED_SUCCESS', { defaultValue: 'Boundaries added to Target Group' }),
        isError: false,
        category: 'Success'
      })
    } catch (error) {
      const sourceList = error.type === 'admin_boundary_error'
        ? adminBoundaries
        : error.type === 'zip_code_error'
          ? zipCodes
          : []
      const names = (Array.isArray(error.data) ? error.data : [])
        .map((entry, index) => (entry && Object.keys(entry).length > 0 ? sourceList[index] : null))
        .filter(Boolean)
        .map(boundary => boundary.name)
        .join(', ')
      const message = names
        ? `${names} already exist in this folder.`
        : (error.data && error.data.detail) || t('GLOBAL.AN_ERROR_OCCURRED')
      uiStore.addNotification({
        text: t('BOUNDARY.BOUNDARY_SAVED_ERROR', { error: message }),
        isError: true,
        category: 'Error'
      })
    } finally {
      if (targetGroup) {
        const realGroup = (dataStore && dataStore.targetGroups && dataStore.targetGroups.find(g => g.pk === targetGroup.pk)) || targetGroup
        await realGroup.load(true)
        if (targetGroup && targetGroup !== realGroup && typeof targetGroup.load === 'function') {
          await targetGroup.load(true)
          targetGroup.expanded = true
        }
        realGroup.expanded = true

        const firstSelected = (zipCodes.length > 0 ? zipCodes[0] : null) || (adminBoundaries.length > 0 ? adminBoundaries[0] : null)
        const remoteDbId = firstSelected ? String(firstSelected.remote_db_id || firstSelected.id) : null

        let boundary = null
        if (remoteDbId) {
          boundary = (realGroup.boundaries && realGroup.boundaries.find(x => String(x.remote_db_id || x.id) === remoteDbId)) ||
                     (realGroup.zipCodeBoundaries && realGroup.zipCodeBoundaries.find(x => String(x.remote_db_id || x.id) === remoteDbId)) ||
                     (targetGroup.boundaries && targetGroup.boundaries.find(x => String(x.remote_db_id || x.id) === remoteDbId)) ||
                     (targetGroup.zipCodeBoundaries && targetGroup.zipCodeBoundaries.find(x => String(x.remote_db_id || x.id) === remoteDbId))
        }

        if (boundary) {
          if (typeof uiStore.clearPreviousSelectionBeforeAdd === 'function') {
            uiStore.clearPreviousSelectionBeforeAdd()
          }
          selectionStore.clearSelectedMenuItems()
          selectionStore.addSelectedMenuItem(boundary)
          selectionStore.select(
            boundary,
            { data: { group: realGroup } }
          )
          uiStore.setBoundsToItem(boundary)
          selectionStore.hideSubTools()
          if (uiStore && typeof uiStore.scrollToSidebarItem === 'function') {
            uiStore.scrollToSidebarItem(boundary, realGroup)
            setTimeout(() => {
              uiStore.scrollToSidebarItem(boundary, realGroup)
            }, 100)
          }
          if (rightSidebarStore) {
            const { getShapePopulationFetchOptions } = require('utils')
            await rightSidebarStore.fetchDataForSinglePolygon(
              boundary.id,
              boundary.name,
              getShapePopulationFetchOptions(boundary)
            )
          }
        } else {
          selectionStore.hideSubTools()
          if (uiStore && typeof uiStore.scrollToSidebarItem === 'function') {
            uiStore.scrollToSidebarItem(realGroup, realGroup)
            setTimeout(() => {
              uiStore.scrollToSidebarItem(realGroup, realGroup)
            }, 100)
          }
        }
      }
      boundaryList.savingBoundaries = false
    }
  }

  return (
    <div className='boundary-list-pane'>
      {/* Pane Header with Title and Close X */}
      <PaneHeader
        icon={placeAdministrativeBoundary}
        title={t('ITEM.BOUNDARY', { defaultValue: 'Choose boundary' })}
        onClose={handleClose}
      />

      {/* Subtitle */}
      <p className='boundary-list-pane__subtitle'>
        {t('GLOBAL.CHOOSE_BOUNDARY_SUBTITLE', { defaultValue: 'Choose a boundary type, then browse areas' })}
      </p>

      {/* Boundary Type Tabs (Administrative vs Postal codes) */}
      <div className='boundary-type-tabs'>
        <button
          type='button'
          className={classnames('boundary-type-tabs__tab', {
            'boundary-type-tabs__tab--active': !boundaryList.useZipCodes
          })}
          disabled={isLoading}
          onClick={() => handleSetZipCodes(false)}
        >
          {t('BOUNDARY.ADMINISTRATIVE', { defaultValue: 'Administrative' })}
        </button>
        <button
          type='button'
          className={classnames('boundary-type-tabs__tab', {
            'boundary-type-tabs__tab--active': boundaryList.useZipCodes
          })}
          disabled={isLoading}
          onClick={() => handleSetZipCodes(true)}
        >
          {t('BOUNDARY.POSTAL_CODES', { defaultValue: 'Postal codes' })}
        </button>
      </div>

      {/* Search Bar */}
      <div className='boundary-search-wrap'>
        <span className='boundary-search-wrap__icon'>
          <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
            <circle cx='11' cy='11' r='8' />
            <line x1='21' y1='21' x2='16.65' y2='16.65' />
          </svg>
        </span>
        <input
          type='text'
          className='boundary-search-wrap__input'
          placeholder={
            boundaryList.useZipCodes
              ? t('BOUNDARY.SEARCH_POSTAL_CODES_PLACEHOLDER', { defaultValue: 'Search postal codes or areas' })
              : t('BOUNDARY.SEARCH_PLACEHOLDER', { defaultValue: 'Search countries or areas' })
          }
          value={boundaryList.search}
          onChange={(e) => {
            boundaryList.search = e.target.value
          }}
          disabled={isLoading}
        />
        {boundaryList.search && (
          <button
            type='button'
            className='boundary-search-wrap__clear'
            onClick={() => {
              boundaryList.search = ''
            }}
            aria-label='Clear search'
            style={{ width: '24px', height: '24px', minWidth: '24px', maxWidth: '24px', padding: 0 }}
          >
            <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
        )}
      </div>

      {/* Section Header (Browse / Search results + X selected) */}
      <div className='boundary-section-header'>
        <span className='boundary-section-header__title'>
          {boundaryList.search !== ''
            ? t('BOUNDARY.SEARCH_RESULTS', { defaultValue: 'Search results' })
            : t('BOUNDARY.BROWSE', { defaultValue: 'Browse' })}
        </span>
        <span className='boundary-section-header__count'>
          {selectedCount} {t('GLOBAL.SELECTED', { defaultValue: 'selected' })}
        </span>
      </div>

      {/* Tree list card */}
      <div className={classnames('boundary-tree-card', { 'boundary-tree-card--loading': isLoading })}>
        {isLoading
          ? (
            <>
              <div className='spinner' style={{ width: 20, height: 20 }} />
              <span>{t('GLOBAL.LOADING', { defaultValue: 'Loading boundaries...' })}</span>
            </>
            )
          : boundaryList.search !== ''
            ? <BoundaryListSearch boundaryList={boundaryList} savingBoundaries={boundaryList.savingBoundaries} />
            : <BoundaryListBrowse boundaryList={boundaryList} savingBoundaries={boundaryList.savingBoundaries} />}
      </div>

      {/* Footer / Add boundary action button */}
      <div className='boundary-list-pane__footer'>
        <button
          type='button'
          className='boundary-list-pane__btn-add'
          disabled={selectedCount <= 0 || boundaryList.savingBoundaries}
          onClick={handleConfirmAdd}
        >
          {boundaryList.savingBoundaries
            ? (
              <>
                <div className='spinner' style={{ width: 14, height: 14 }} />
                <span>{t('GLOBAL.ADDING', { defaultValue: 'Adding...' })}</span>
              </>
              )
            : (
              <span>{t('GLOBAL.ADD_BOUNDARY', { defaultValue: 'Add boundary' })}</span>
              )}
        </button>
      </div>
    </div>
  )
}

export default withTranslation()(
  inject('selectionStore', 'uiStore', 'rightSidebarStore', 'dataStore')(
    observer(BoundaryListPane)
  )
)
